import {
  CalendarItemUnion,
  CalendarItemUnionUpdate,
} from '@akin-home/calendar/combined';
import { mutateDateTimeFromTimeString } from '@akin-home/dates';
import { addHours } from 'date-fns';
import produce from 'immer';
import { useReducer } from 'react';

enum CollapsableSections {
  REPEAT,
  REMINDER,
  TAGS,
  COLOUR,
  PHOTO,
}

interface State {
  item?: CalendarItemUnion;
  loading: boolean;
  mode: 'create' | 'edit';
  sections: Record<CollapsableSections, boolean>;
}

type Action =
  | {
      type: 'set_item';
      payload: {
        item: CalendarItemUnion;
      };
    }
  | {
      type: 'update_item';
      payload: {
        item: CalendarItemUnionUpdate;
      };
    }
  | {
      type: 'toggle_section';
      payload: {
        section: CollapsableSections;
      };
    };

type Dispatch = React.Dispatch<Action>;

const reducer = produce((state: State, action: Action) => {
  switch (action.type) {
    case 'set_item': {
      state.item = action.payload.item;
      state.loading = false;
      break;
    }
    case 'update_item': {
      if (!state.item) return;

      const { start_date, start_time, end_date, end_time, ...data } =
        action.payload.item;

      if (start_date) {
        state.item.start.setDate(new Date(start_date).getDate());

        if ('end' in state.item) {
          state.item.end = addHours(state.item.start, 1);
        }
      }

      if (start_time) {
        mutateDateTimeFromTimeString(state.item.start, start_time);

        if ('end' in state.item) {
          state.item.end = addHours(state.item.start, 1);
        }
      }

      if ('end' in state.item) {
        if (end_date) {
          state.item.end.setDate(new Date(end_date).getDate());
        }

        if (end_time) {
          mutateDateTimeFromTimeString(state.item.end, end_time);
        }
      }

      state.item = {
        ...state.item,
        ...data,
      } as CalendarItemUnion;
      break;
    }
    case 'toggle_section': {
      const section = action.payload.section;
      state.sections[section] = !state.sections[section];
      break;
    }
  }
});

interface Props {
  item?: CalendarItemUnion;
  mode: 'edit' | 'create';
}

const all_sections_closed = {
  [CollapsableSections.COLOUR]: false,
  [CollapsableSections.PHOTO]: false,
  [CollapsableSections.REPEAT]: false,
  [CollapsableSections.TAGS]: false,
  [CollapsableSections.REMINDER]: false,
};

export const useCalendarItemForm = (props: Props): [State, Dispatch] => {
  const [state, dispatch] = useReducer(reducer, {
    item: props.item,
    loading: !props.item,
    mode: props.mode,
    sections: all_sections_closed,
  });

  return [state, dispatch];
};

export {
  State as CalendarItemFormState,
  Dispatch as CalenderItemFormDispatch,
  CollapsableSections as CalendarItemFormCollapsableSection,
};
